/**
 * Extraction Store
 * Manages state for element extraction functionality
 */

import { create } from 'zustand';
import type { FamilyInfo } from '@/services/extractionService';

export interface ExtractedElement {
  dbId: number;
  name: string;
  category: string;
  family?: string;
  type?: string;
  level?: string;
  properties: Record<string, any>;
}

interface ExtractionState {
  // State
  categories: string[];
  selectedCategory: string;
  families: FamilyInfo[];
  selectedFamily: string;
  extractedElements: ExtractedElement[];
  filteredElements: ExtractedElement[];
  filterText: string;
  isExtracting: boolean;
  extractionProgress: number;
  isLoadingFamilies: boolean;
  extractionMode: 'category' | 'family';
  
  // Actions
  setCategories: (categories: string[]) => void;
  setSelectedCategory: (category: string) => void;
  setFamilies: (families: FamilyInfo[]) => void;
  setSelectedFamily: (family: string) => void;
  setExtractedElements: (elements: ExtractedElement[]) => void;
  setFilteredElements: (elements: ExtractedElement[]) => void;
  setFilterText: (text: string) => void;
  setIsExtracting: (isExtracting: boolean) => void;
  setExtractionProgress: (progress: number) => void;
  setIsLoadingFamilies: (loading: boolean) => void;
  setExtractionMode: (mode: 'category' | 'family') => void;
  clearExtraction: () => void;
}

export const useExtractionStore = create<ExtractionState>((set) => ({
  // Initial state
  categories: [],
  selectedCategory: '',
  families: [],
  selectedFamily: '',
  extractedElements: [],
  filteredElements: [],
  filterText: '',
  isExtracting: false,
  extractionProgress: 0,
  isLoadingFamilies: false,
  extractionMode: 'category',
  
  // Actions
  setCategories: (categories) => set({ categories }),
  setSelectedCategory: (category) => set({ selectedCategory: category, selectedFamily: '', families: [] }),
  setFamilies: (families) => set({ families }),
  setSelectedFamily: (family) => set({ selectedFamily: family }),
  setExtractedElements: (elements) => set({ 
    extractedElements: elements,
    filteredElements: elements 
  }),
  setFilteredElements: (elements) => set({ filteredElements: elements }),
  setFilterText: (text) => set({ filterText: text }),
  setIsExtracting: (isExtracting) => set({ isExtracting }),
  setExtractionProgress: (progress) => set({ extractionProgress: progress }),
  setIsLoadingFamilies: (loading) => set({ isLoadingFamilies: loading }),
  setExtractionMode: (mode) => set({ extractionMode: mode }),
  clearExtraction: () => set({
    extractedElements: [],
    filteredElements: [],
    filterText: '',
    extractionProgress: 0,
    selectedFamily: ''
  })
}));